/**
 * Pre-flight for the campaign scheduling wizard (H3).
 *
 * The submit handler used to open with `if (!selectedWorkflowId || !name ||
 * !scheduledStart) return` — a silent no-op, so a click on a disabled-looking
 * button did nothing and said nothing. Worse, the two conditions it did NOT
 * check both produce a campaign that is accepted by the API and then cannot
 * send: no sender email (the executor errors with "Sender email not found") and
 * a scheduled start already in the past.
 *
 * Pure so it can be tested without the modal.
 */

/** A scheduled start this far behind "now" still counts as valid, to absorb the
 *  seconds between reviewing the summary and pressing the button. */
export const PAST_GRACE_MS = 60_000

export interface LaunchPreflightInput {
  selectedWorkflowId: number | null
  /** The inboxes picked, in order; empty means none (rotation_email.md). */
  selectedSenderEmailIds: number[]
  /** How many verified senders exist — changes the advice, not the verdict. */
  verifiedSenderCount: number
  audienceType: 'list' | 'all'
  selectedListIds: number[]
  name: string
  /** The `datetime-local` value, i.e. local wall-clock, or `''`. */
  scheduledStart: string
  /** Step 3 resolved: the signature that every email goes out with is non-blank. */
  signatureConfirmed: boolean
  /** A Representative's campaign is routed to an approver rather than launched. */
  needsApprover: boolean
  /** The chosen approver, when one is needed. */
  approverId: number | null
}

/**
 * Every blocker, in the order the wizard's steps present them. An empty array
 * means the campaign can be scheduled.
 */
export function launchPreflightIssues(input: LaunchPreflightInput): string[] {
  const {
    selectedWorkflowId,
    selectedSenderEmailIds,
    verifiedSenderCount,
    audienceType,
    selectedListIds,
    name,
    scheduledStart,
    signatureConfirmed,
    needsApprover,
    approverId,
  } = input

  const issues: string[] = []

  if (!selectedWorkflowId) issues.push('Choose an active workflow.')

  if (selectedSenderEmailIds.length === 0) {
    issues.push(
      verifiedSenderCount === 0
        ? 'No verified sender email — add and verify one under Mailboxes first.'
        : 'Choose the verified sender email to send from.',
    )
  }

  if (!signatureConfirmed) issues.push('Confirm a signature on the Signature step')

  if (audienceType === 'list' && selectedListIds.length === 0) {
    issues.push('Pick at least one contact list.')
  }

  if (!name.trim()) issues.push('Give the campaign a name.')

  if (!scheduledStart) {
    issues.push('Set a scheduled start.')
  } else if (Number.isNaN(new Date(scheduledStart).getTime())) {
    issues.push('The scheduled start is not a valid date and time.')
  }
  // A start in the past is no longer a blocker. It is clamped to "now" on
  // submit (effectiveStartIso), so it means "start immediately" rather than
  // being a mistake to correct. The review step says which of the two will
  // happen, so the clamp is never silent. (AB#22455)

  if (needsApprover && approverId == null) issues.push('Choose who should review this campaign')

  return issues
}

/**
 * How far ahead the wizard pre-fills the scheduled start.
 *
 * Zero: a campaign runs immediately unless the user chooses otherwise, which
 * is what people expect of a Launch button (AB#22455). The backend's create
 * route launches synchronously when the start is already due, so "now" really
 * does mean now rather than a wait for the next 30-second beat tick.
 *
 * This was 15 minutes, for a real reason that no longer applies. The default
 * used to be "now, to the minute" captured when the wizard opened, which goes
 * stale while the user picks a workflow, an audience and a sender, and the
 * pre-flight then showed a red "scheduled start is in the past" on a field
 * they had never touched (AB#22322). A lead time hid that. Clamping the start
 * to max(now, chosen) at submit removes the cause instead, so the default can
 * go back to meaning now.
 */
export const DEFAULT_START_LEAD_MS = 0

/**
 * The start to actually send: max(now, chosen).
 *
 * Two problems, one clamp. A pre-filled "now" goes stale while the wizard is
 * being filled in, and a deliberately past time would otherwise sit waiting
 * for a moment that has gone. Either way the honest reading is "start now",
 * and the backend treats an already-due start as an immediate launch.
 *
 * A future time is returned untouched: this can only ever move a start
 * forward to the present, never earlier than the user asked.
 */
export function effectiveStartIso(scheduledStart: string, nowMs: number, tz?: string): string {
  const chosenMs = wallClockToMs(scheduledStart, tz)
  if (Number.isNaN(chosenMs)) return new Date(nowMs).toISOString()
  return new Date(Math.max(nowMs, chosenMs)).toISOString()
}

/**
 * Whether the campaign will start as soon as it is created, so the review step
 * can say "Starts immediately" instead of showing a time that has passed.
 * PAST_GRACE_MS of slack, because a minute-granularity picker set to the
 * current minute is already a few seconds behind by the time it is read.
 */
export function startsImmediately(scheduledStart: string, nowMs: number, tz?: string): boolean {
  const chosenMs = wallClockToMs(scheduledStart, tz)
  return Number.isNaN(chosenMs) || chosenMs <= nowMs + PAST_GRACE_MS
}

/** The browser's IANA zone, or 'UTC' when it cannot be resolved. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Whether `tz` is an IANA zone this runtime understands. */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** UTC offset (ms, east positive) of `tz` at the instant `utcMs`. */
function tzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs))
  const get = (t: string) => Number(parts.find(p => p.type === t)?.value)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'))
  return asUtc - Math.floor(utcMs / 1000) * 1000
}

/**
 * Epoch ms of a `YYYY-MM-DDTHH:mm` wall-clock value read in `tz`.
 * Without `tz` (or with an invalid one) the value is read in the browser zone,
 * which is the legacy behaviour. NaN when the value does not parse.
 */
export function wallClockToMs(wall: string, tz?: string): number {
  if (!tz || !isValidTimeZone(tz)) return new Date(wall).getTime()
  const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!m) return new Date(wall).getTime()
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  // Two passes so a value near a DST change resolves to the right offset.
  let ms = guess - tzOffsetMs(guess, tz)
  ms = guess - tzOffsetMs(ms, tz)
  return ms
}

/** `YYYY-MM-DDTHH:mm` wall-clock reading of the instant `ms` in `tz`. */
export function msToWallClock(ms: number, tz: string): string {
  const shifted = new Date(ms + tzOffsetMs(ms, tz))
  return shifted.toISOString().slice(0, 16)
}

/** Zone list for the picker: common zones plus the browser zone and any extra. */
export function timeZoneChoices(selected: string[] = []): { value: string; label: string }[] {
  const base = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai',
    'Asia/Kolkata', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney',
  ]
  const all = [...selected, ...base].filter((z, i, a) => z && a.indexOf(z) === i && isValidTimeZone(z))
  return all.map(z => ({ value: z, label: z.replace(/_/g, ' ') }))
}

/** Local wall-clock `datetime-local` value, DEFAULT_START_LEAD_MS ahead of `now`. */
export function defaultScheduledStart(now: Date, leadMs: number = DEFAULT_START_LEAD_MS, tz?: string): string {
  if (tz && isValidTimeZone(tz)) return msToWallClock(now.getTime() + leadMs, tz)
  const d = new Date(now.getTime() + leadMs)
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}
